<template>
  <div id="criteria-list">
    <v-overlay :value="OverlayLoadingFlag">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- Dialogs -->
    <v-dialog v-model="AddRecordPrompt" persistent max-width="1300px">
      <criteria-create
        :AddRecordPrompt="AddRecordPrompt"
        @hideDialog="hideAddRecordPrompt"
        v-if="AddRecordPrompt"
      ></criteria-create>
    </v-dialog>

    <v-dialog v-model="EditRecordPrompt" persistent max-width="1300px">
      <criteria-edit
        :EditRecordPrompt="EditRecordPrompt"
        :recordData="selectedData"
        @hideDialog="hideEditRecordPrompt"
        v-if="EditRecordPrompt"
      ></criteria-edit>
    </v-dialog>

    <v-dialog v-model="EditRecordPrompt" persistent max-width="1300px">
      <criteria-edit
        :EditRecordPrompt="EditRecordPrompt"
        :recordData="selectedData"
        @hideDialog="hideEditRecordPrompt"
        v-if="EditRecordPrompt"
      ></criteria-edit>
    </v-dialog>

    <!-- Premium Header Card with SpeechCraft Animation -->
    <v-card elevation="20" class="header-card mb-6 overflow-hidden">
      <div class="header-gradient">
        <div class="header-backdrop-effect"></div>
        <div class="header-content pa-8">
          <v-row align="center" justify="space-between">
            <!-- Left Side: Title and Icon -->
            <v-col cols="12" md="8" class="d-flex align-center">
              <div class="header-icon-box">
                <v-icon size="40px" color="white">mdi-target-variant</v-icon>
              </div>
              <div class="ml-4">
                <h1 class="header-title white--text mb-0">
                  Efficiency Criteria
                </h1>
                <p class="header-subtitle white--text mb-0">
                  Manage and organize all efficiency criteria in your system
                </p>
              </div>
            </v-col>

            <!-- Right Side: Action Buttons -->
            <v-col cols="12" md="4" class="text-right">
              <v-btn
                icon
                class="action-btn"
                @click="refreshPageData"
                title="Refresh"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
              <v-btn
                icon
                class="action-btn"
                @click="AddRecordPrompt = true"
                title="Add New Criteria"
              >
                <v-icon>mdi-plus-circle</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-card>

    <!-- Data Table Card -->
    <v-card elevation="12" class="table-card" v-if="tableData1.length > 0">
      <v-container class="pa-6">
        <v-row class="mb-4">
          <v-col cols="12" class="d-flex align-center justify-space-between">
            <div>
              <h3 class="table-title">
                <v-icon class="mr-2">mdi-table</v-icon>
                Efficiency Criteria ({{ tableData1.length }} Records)
              </h3>
              <div class="text-caption grey--text text--darken-1 font-weight-medium mt-1">
                <v-icon small color="#3A67B1">mdi-cursor-move</v-icon> Drag handle or use Up/Down arrows to reorder criteria sequence
              </div>
            </div>
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              label="Search in table"
              single-line
              hide-details
              dense
              outlined
              class="search-field"
              style="max-width: 300px"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-data-table
          class="criteria-table"
          v-model="selected"
          :headers="tableColumns1"
          :items="tableData1"
          :single-select="tableOptions1.SingleSelectFlag"
          :search="search"
          :footer-props="{ itemsPerPageOptions: [10, 25, 50, 100, -1] }"
          :item-key="tableOptions1.ItemKey"
          multi-sort
        >
          <template v-slot:body="{ items }">
            <draggable
              v-model="tableData1"
              tag="tbody"
              handle=".drag-handle"
              @end="updateCriteriaOrder"
            >
              <tr v-for="(item, index) in items" :key="item.EfficiencyCriteriaId" class="hover-row">
                <!-- Status -->
                <td>
                  <v-chip
                    :color="item.ActiveStatusTxt === 'Active' ? '#4caf50' : '#f44336'"
                    text-color="white"
                    small
                    class="font-weight-bold"
                  >
                    <v-icon left small>{{
                      item.ActiveStatusTxt === 'Active'
                        ? 'mdi-check-circle'
                        : 'mdi-close-circle'
                    }}</v-icon>
                    {{ item.ActiveStatusTxt }}
                  </v-chip>
                </td>

                <!-- Order (Drag Handle + Up/Down) -->
                <td>
                  <div class="d-flex align-center">
                    <v-icon class="drag-handle mr-2" style="cursor: grab; color: #3A67B1;" title="Drag to reorder">mdi-cursor-move</v-icon>
                    <v-chip color="#e0f2fe" style="color: #0369a1 !important;" small class="font-weight-bold mr-1">
                      #{{ index + 1 }}
                    </v-chip>
                    <v-btn icon x-small color="primary" :disabled="index === 0" @click="moveRow(index, index - 1)" title="Move Up">
                      <v-icon>mdi-chevron-up</v-icon>
                    </v-btn>
                    <v-btn icon x-small color="primary" :disabled="index === items.length - 1" @click="moveRow(index, index + 1)" title="Move Down">
                      <v-icon>mdi-chevron-down</v-icon>
                    </v-btn>
                  </div>
                </td>

                <!-- Event Type -->
                <td>
                  <v-chip
                    :color="
                      item.EfficiencySubCriteriaEventTypeTxt === 'Zone Event'
                        ? '#0097d7'
                        : item.EfficiencySubCriteriaEventTypeTxt === 'National Event'
                        ? '#edbe38'
                        : '#3a67b1'
                    "
                    text-color="white"
                    small
                    class="font-weight-bold"
                  >
                    <v-icon left small>mdi-calendar-star</v-icon>
                    {{ item.EfficiencySubCriteriaEventTypeTxt || 'Default' }}
                  </v-chip>
                </td>

                <!-- Is Completed -->
                <td>
                  <v-chip
                    :color="item.IsCompletedTxt === 'Yes' ? '#2196f3' : '#f44336'"
                    text-color="white"
                    small
                    class="font-weight-bold"
                  >
                    <v-icon left small>{{
                      item.IsCompletedTxt === 'Yes' ? 'mdi-check' : 'mdi-close'
                    }}</v-icon>
                    {{ item.IsCompletedTxt }}
                  </v-chip>
                </td>

                <!-- Is Automatic -->
                <td>
                  <v-chip
                    :color="item.IsAutomaticTxt === 'Yes' ? '#2196f3' : '#f44336'"
                    text-color="white"
                    small
                    class="font-weight-bold"
                  >
                    <v-icon left small>{{
                      item.IsAutomaticTxt === 'Yes' ? 'mdi-check' : 'mdi-close'
                    }}</v-icon>
                    {{ item.IsAutomaticTxt }}
                  </v-chip>
                </td>

                <!-- Criteria Short Name -->
                <td class="font-weight-bold">{{ item.EfficiencyCriteriaShortName || '—' }}</td>

                <!-- Criteria Name -->
                <td class="font-weight-bold">{{ item.EfficiencyCriteriaName }}</td>

                <!-- Field Name -->
                <td>
                  <span class="font-weight-600" :title="item.FieldName">
                    {{ item.FieldName ? item.FieldName : '—' }}
                  </span>
                </td>

                <!-- Description -->
                <td>
                  <div class="text-truncate" style="max-width: 200px" :title="item.Description">
                    {{ item.Description ? item.Description : '—' }}
                  </div>
                </td>

                <!-- Actions -->
                <td>
                  <div class="action-cell">
                    <v-btn
                      x-small
                      icon
                      color="primary"
                      @click="editData(item)"
                      title="Edit"
                      class="action-icon-btn"
                    >
                      <v-icon small>mdi-pencil-circle</v-icon>
                    </v-btn>
                    <v-btn
                      x-small
                      icon
                      color="error"
                      @click="deleteConfirmAlert(item)"
                      title="Delete"
                      class="action-icon-btn"
                    >
                      <v-icon small>mdi-trash-can-outline</v-icon>
                    </v-btn>
                  </div>
                </td>
              </tr>
            </draggable>
          </template>
        </v-data-table>

          <!-- Is Automatic Column -->
          <template v-slot:item.IsAutomaticTxt="{ item }">
            <v-chip
              :color="item.IsAutomaticTxt === 'Yes' ? '#2196f3' : '#f44336'"
              text-color="white"
              small
              class="font-weight-bold"
            >
              <v-icon left small>{{
                item.IsAutomaticTxt === "Yes" ? "mdi-check" : "mdi-close"
              }}</v-icon>
              {{ item.IsAutomaticTxt }}
            </v-chip>
          </template>

          <!-- Is Automatic Column -->
          <template v-slot:item.IsCompletedTxt="{ item }">
            <v-chip
              :color="item.IsCompletedTxt === 'Yes' ? '#2196f3' : '#f44336'"
              text-color="white"
              small
              class="font-weight-bold"
            >
              <v-icon left small>{{
                item.IsCompletedTxt === "Yes" ? "mdi-check" : "mdi-close"
              }}</v-icon>
              {{ item.IsCompletedTxt }}
            </v-chip>
          </template>
          <!-- Field Name Column -->
          <template v-slot:item.FieldName="{ item }">
            <span class="font-weight-600" :title="item.FieldName">
              {{ item.FieldName ? item.FieldName : "—" }}
            </span>
          </template>

          <!-- Auto Description Column -->
          <template v-slot:item.Description="{ item }">
            <div
              class="text-truncate"
              style="max-width: 200px"
              :title="item.Description"
            >
              {{ item.Description ? item.Description : "—" }}
            </div>
          </template>

          <!-- Actions Column -->
          <template v-slot:item.actions="{ item }">
            <div class="action-cell">
              <v-btn
                x-small
                icon
                color="primary"
                @click="editData(item)"
                title="Edit"
                class="action-icon-btn"
              >
                <v-icon small>mdi-pencil-circle</v-icon>
              </v-btn>
              <v-btn
                x-small
                icon
                color="error"
                @click="deleteConfirmAlert(item)"
                title="Delete"
                class="action-icon-btn"
              >
                <v-icon small>mdi-trash-can-outline</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-container>
    </v-card>

    <!-- Empty State -->
    <v-card elevation="12" class="empty-state-card" v-else>
      <v-container class="pa-12 text-center">
        <v-icon size="80" color="grey lighten-2">mdi-inbox-outline</v-icon>
        <h3 class="mt-4 text-h6 grey--text text--darken-2">No records found</h3>
        <p class="mt-2 text-body2 grey--text">
          Try creating a new efficiency criteria to get started
        </p>
      </v-container>
    </v-card>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";
import draggable from "vuedraggable";
import CriteriaCreate from "@/view/pages/erp/efficiency/criteria/CriteriaCreate.vue";
import CriteriaEdit from "@/view/pages/erp/efficiency/criteria/CriteriaEdit.vue";

export default {
  mixins: [common],
  components: { CriteriaCreate, CriteriaEdit, draggable },
  data() {
    return {
      LoadingFlag: false,
      SearchFlag: false,
      uploadPercentage: 0,
      AddRecordPrompt: false,
      EditRecordPrompt: false,
      ReorderPrompt: false,
      reorderLoading: false,
      reorderItems: [],

      valid1: true,
      OverlayLoadingFlag: false,

      tableData1: [],
      tableColumns1: [],
      tableOptions1: [],
      selected: [],
      search: "",
      selectedData: {},

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      JciYearCode: "",
      YearName: "",
    };
  },
  mounted() {},
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        var flag = tr.PagePermissionFlag;
        console.log("PagePermissionFlag=" + flag);

        if (!flag) {
          this.$router.push("/access-denied");
        }
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        this.CurrentYearId = CurrentJciYearId;
        this.searchForm();
      }
    },
  },
  methods: {
    moveRow(fromIndex, toIndex) {
      if (toIndex < 0 || toIndex >= this.tableData1.length) return;
      const element = this.tableData1.splice(fromIndex, 1)[0];
      this.tableData1.splice(toIndex, 0, element);
      this.updateCriteriaOrder();
    },
    updateCriteriaOrder() {
      console.log("updateCriteriaOrder is called");
      this.reorderLoading = true;

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/criteria/reorder";

      var CriteriaId = this.tableData1.map(function (item) {
        return item.EfficiencyCriteriaId;
      });

      var upload = {
        CriteriaId: CriteriaId,
      };
      console.log("upload=" + JSON.stringify(upload));

      var thisIns = this;
      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));
          thisIns.reorderLoading = false;
          if (response.data.flag == 1 || response.data.flag == true) {
            thisIns.toast("success", "Criteria order updated successfully.");
          } else {
            thisIns.toast("error", response.data.output || response.data.error);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.reorderLoading = false;
          thisIns.toast("error", error.message || "Failed to update order.");
        });
    },
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "full_efficiency",
        Action: "list_2",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    backPage() {
      this.$router.go(-1);
    },
    searchForm() {
      console.log("searchForm is called");
      this.LoadingFlag = true;
      this.SearchFlag = true;
      this.tableData1 = [];

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/criteria/lists";
      var upload = {
        UserInterface: 1,
      };
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      const thisIns = this;
      var output = "";
      var records = {};
      var successTxt = "";
      var errorTxt = "";
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          // thisIns.pageLoadingOff()
          thisIns.LoadingFlag = false;
          thisIns.SearchFlag = false;

          output = response.data.output;
          flag = response.data.flag;
          output = response.data.output;
          records = response.data.records;
          console.log("output=" + output + ", flag=" + flag);

          console.log({ records });

          var options = records.TableOptions;
          console.log({ options });
          thisIns.tableOptions1 = options;

          if (flag == 1) {
            thisIns.tableColumns1 = records.TableHeader;
            thisIns.tableData1 = records.TableData;
            thisIns.toast("success", output);
          } else {
            thisIns.toast("error", output);
          }
        })
        .catch(function (error) {
          thisIns.LoadingFlag = false;
          thisIns.SearchFlag = false;

          console.log("error=" + error);
          thisIns.toast("error", error);
        });
    },
    hideAddRecordPrompt(flag) {
      console.log("hideAddRecordPrompt called");
      this.AddRecordPrompt = false;
      if (flag) {
        this.searchForm();
      }
    },
    editData(tr) {
      console.log("editData called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));

      if (n1 > 0) {
        this.EditRecordPrompt = true;
        this.selectedData = tr;
      } else {
        var message = "Kindly select one city to edit";
        this.toast("error", message);
      }
    },
    hideEditRecordPrompt(flag) {
      console.log("hideEditRecordPrompt called");
      this.EditRecordPrompt = false;
      if (flag) {
        this.searchForm();
      }
    },
    deleteConfirmAlert(tr) {
      console.log("deleteConfirmAlert called");
      var message = "";
      // var tr = this.selected
      // var n1 = tr.length
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));

      if (n1 > 0) {
        var htmlTxt = "";
        htmlTxt +=
          "<br/> Criteria Name <b> " + tr.EfficiencyCriteriaName + "</b>";

        Swal.fire({
          title: "Do you want to continue?",
          text: "Please verify all the information before proceeding.",
          icon: "warning",
          html: htmlTxt,
          showCancelButton: true,
          confirmButtonText: `Delete`,
          confirmButtonColor: "#d33",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#3085d6",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            this.deleteData(tr);
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        if (n1 == 0) {
          message += "Kindly select one record to delete. ";
        }
        this.sweetAlert("error", message, false);
      }
    },
    deleteData(data) {
      console.log("deleteData is called");

      var CriteriaCode = data.EfficiencyCriteriaId;

      console.log("CriteriaCode=" + CriteriaCode);

      if (CriteriaCode > 0) {
        this.OverlayLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/criteria/destroy";
        var upload = {
          UserInterface: 1,
          Criteria: CriteriaCode,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.OverlayLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            console.log("output=" + output + ", flag=" + flag);

            if (flag == 1) {
              thisIns.sweetAlert("success", output, true);
              thisIns.searchForm();
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.OverlayLoadingFlag = false;
          });
      } else {
        var message = "Event Id should not be empty. ";
        this.sweetAlert("error", message, false);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>
<style lang="scss">
// Color Palette - JCI Brand Colors
$navy: #3a67b1;
$aqua: #0097d7;
$gold: #edbe38;
$seaform: #56bda3;
$text-off-black: #0a0f29;
$text-white: #ffffff;
$danger-color: #e74c3c;
$border-color: #e2e8f0;
$success-color: #4caf50;

// ============================================
// KEYFRAME ANIMATIONS
// ============================================
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-12px);
  }
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
  100% {
    opacity: 1;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

// ============================================
// MAIN CONTAINER STYLES
// ============================================
#criteria-list {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding: 30px 15px;

  // ============================================
  // HEADER CARD
  // ============================================
  .header-card {
    background: linear-gradient(
      135deg,
      $navy 0%,
      $aqua 50%,
      $navy 100%
    ) !important;
    border-radius: 20px;
    overflow: hidden;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    border: none;
    box-shadow: 0 20px 60px rgba(58, 103, 177, 0.5),
      0 0 40px rgba(0, 151, 215, 0.3);

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        45deg,
        transparent 30%,
        rgba(255, 255, 255, 0.1) 50%,
        transparent 70%
      );
      animation: shimmer 3s infinite;
    }

    .header-gradient {
      position: relative;
      z-index: 1;
    }

    .header-backdrop-effect {
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.15) 0%,
        transparent 70%
      );
      animation: rotate 25s linear infinite;
    }

    .header-content {
      position: relative;
      z-index: 2;
    }
  }

  .header-icon-box {
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.25) 0%,
      rgba(255, 255, 255, 0.15) 100%
    );
    backdrop-filter: blur(15px);
    border-radius: 18px;
    padding: 14px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.4);
    animation: float 4s ease-in-out infinite;
    border: 2px solid rgba(255, 255, 255, 0.3);
  }

  .header-title {
    font-size: 36px;
    font-weight: 900;
    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3), 0 0 20px rgba(0, 151, 215, 0.3);
    animation: slideInLeft 0.8s ease-out;
    letter-spacing: 0.8px;
    color: $text-white;
  }

  .header-subtitle {
    font-size: 17px;
    opacity: 0.98;
    animation: slideInLeft 1s ease-out 0.1s backwards;
    letter-spacing: 0.4px;
    color: $text-white;
    font-weight: 500;
  }

  .action-btn {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    background: rgba(255, 255, 255, 0.15) !important;
    border: 2px solid rgba(255, 255, 255, 0.3) !important;
    backdrop-filter: blur(10px);
    color: white !important;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 24px rgba(0, 0, 0, 0.3) !important;
      background: rgba(255, 255, 255, 0.25) !important;
    }
  }

  // ============================================
  // TABLE CARD
  // ============================================
  .table-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%) !important;
    box-shadow: 0 16px 48px rgba(58, 103, 177, 0.12) !important;
    border: none !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s backwards;
  }

  .table-title {
    font-size: 18px;
    font-weight: 700;
    color: $navy;
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
  }

  .search-field {
    transition: all 0.3s ease;
    border-radius: 8px;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
    }

    &::v-deep input {
      font-size: 14px !important;
    }
  }

  .criteria-table {
    &::v-deep {
      .v-data-table__wrapper {
        border-radius: 8px;
        overflow: hidden;
        min-height: 700px;
      }

      thead {
        background: linear-gradient(135deg, $navy 0%, $aqua 100%) !important;

        th {
          color: white !important;
          font-weight: 700 !important;
          font-size: 20px !important;
          text-transform: uppercase !important;
          letter-spacing: 0.6px !important;
          padding: 35px 28px !important;
          border: none !important;
          background: linear-gradient(135deg, $navy 0%, $aqua 100%) !important;
          height: 95px !important;
        }
      }

      tbody {
        tr {
          border-bottom: 1px solid $border-color;
          transition: all 0.3s ease;
          height: 120px !important;

          &:hover {
            background: linear-gradient(
              90deg,
              rgba(0, 151, 215, 0.12) 0%,
              transparent 100%
            ) !important;
            box-shadow: inset 0 0 0 1px rgba(0, 151, 215, 0.15);
          }

          td {
            padding: 30px 28px !important;
            color: $text-off-black;
            font-size: 20px !important;
            font-weight: 500;
            line-height: 2;
            display: flex !important;
            align-items: center !important;
            vertical-align: middle !important;
          }
        }

        tr:nth-child(even) {
          background: #f8f9fc;
        }
      }

      .v-data-table__checkbox {
        margin-right: 12px;
      }
    }
  }

  // ============================================
  // STATUS CHIPS & ACTION BUTTONS
  // ============================================
  .action-cell {
    display: flex;
    gap: 6px;
    align-items: center;
  }

  .action-icon-btn {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    background: linear-gradient(
      135deg,
      rgba(0, 0, 0, 0.05),
      rgba(0, 0, 0, 0.02)
    ) !important;

    &:hover {
      transform: scale(1.15) !important;
      background: linear-gradient(
        135deg,
        rgba(0, 0, 0, 0.1),
        rgba(0, 0, 0, 0.05)
      ) !important;
    }
  }

  // ============================================
  // EMPTY STATE
  // ============================================
  .empty-state-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%) !important;
    box-shadow: 0 16px 48px rgba(58, 103, 177, 0.12) !important;
    border: none !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s backwards;

    &::v-deep {
      .v-icon {
        animation: pulse 2s ease-in-out infinite;
      }
    }
  }

  // ============================================
  // RESPONSIVE DESIGN
  // ============================================
  @media (max-width: 960px) {
    padding: 20px 12px;

    .header-title {
      font-size: 28px;
    }

    .header-subtitle {
      font-size: 14px;
    }

    .header-icon-box {
      padding: 10px;
    }

    .table-title {
      font-size: 16px;
    }
  }

  @media (max-width: 600px) {
    padding: 16px 10px;

    .header-card {
      margin-bottom: 16px;

      .header-content {
        padding: 16px !important;
      }
    }

    .header-title {
      font-size: 22px;
    }

    .header-subtitle {
      font-size: 13px;
    }

    .search-field {
      max-width: 100% !important;
      margin-top: 12px;
    }

    .criteria-table {
      &::v-deep {
        font-size: 12px;

        th,
        td {
          padding: 8px 6px !important;
        }
      }
    }

    .action-cell {
      flex-direction: column;
      gap: 3px;
    }
  }
}
</style>